import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import useFormHook from '@shared/hooks/useFormHook';
import { templateDocumentsService } from '../services/templateDocuments';
import { notifySuccess } from '@config/tostifyConfig/tostifyConfig';

const TYPE_DOC_OPTIONS = [
    { value: 'HR_CONTRACT', label: 'HR_CONTRACT' },
    { value: 'HR_ANEX', label: 'HR_ANEX' },
    { value: 'HR_LEAVE', label: 'HR_LEAVE' },
];

export default function TemplateDocumentsForm({ onClose, modalType, selectedRecord, onConfirm }) {
    const { t } = useTranslation();
    const { modalRef } = useFormHook({}, onClose);
    const fileInputRef = useRef(null);

    const isEdit = modalType === 'edit';
    const [typeDoc, setTypeDoc] = useState(isEdit ? (selectedRecord?.typeDoc || '') : 'HR_CONTRACT');
    const [name, setName] = useState(isEdit ? (selectedRecord?.name || '') : '');
    const [description, setDescription] = useState(isEdit ? (selectedRecord?.description || '') : '');
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedFileName, setSelectedFileName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isEdit) {
            setTypeDoc(selectedRecord?.typeDoc || '');
            setName(selectedRecord?.name || '');
            setDescription(selectedRecord?.description || '');
            setSelectedFile(null);
            setSelectedFileName('');
            setError(null);
            return;
        }

        // Keep add mode always empty/default when modal opens.
        setTypeDoc('HR_CONTRACT');
        setName('');
        setDescription('');
        setSelectedFile(null);
        setSelectedFileName('');
        setError(null);
    }, [isEdit, selectedRecord]);

    const handleChooseFile = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        setSelectedFile(file || null);
        setSelectedFileName(file?.name || '');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        try {
            setLoading(true);
            setError(null);

            if (isEdit) {
                if (!selectedRecord?.id) {
                    setError(t('common.errorMessage') || 'Missing record id.');
                    return;
                }
                if (!selectedFile) {
                    setError(t('common.required') || 'File is required.');
                    return;
                }

                await templateDocumentsService.updateTemplateDocumentFile(selectedRecord.id, selectedFile);
                notifySuccess(t('common.updatedSuccessfully') || 'Updated successfully');
            } else {
                if (!typeDoc || !name.trim() || !description.trim() || !selectedFile) {
                    setError(t('common.required') || 'All fields are required.');
                    return;
                }

                const formData = new FormData();
                formData.append('typeDoc', typeDoc);
                formData.append('description', description.trim());
                formData.append('name', name.trim());
                formData.append('file', selectedFile);

                await templateDocumentsService.createTemplateDocument(formData);
                notifySuccess(t('common.createdSuccessfully') || 'Created successfully');
            }

            await onConfirm?.();
            onClose?.();
        } catch (err) {
            setError(err?.response?.data?.message || err?.response?.data || err?.message || t('common.errorMessage'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>

                    <div className="modal-body">
                        <h4 className="modal-title">
                            {isEdit
                                ? (t('common.edit') || 'Edit')
                                : (t('common.add') || 'Add')}
                        </h4>

                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}
                        <form id="templateDocumentsForm" className="region-form" onSubmit={handleSubmit}>
                            <div className="flex-column">
                                <div className="form-section width-90">
                                    <div className="form-group">
                                        <span className="span-bold">{t('nav.administrator.templateDocuments.typeDoc') || 'Type of document'}</span>
                                        <select
                                            className="small-select"
                                            value={typeDoc}
                                            onChange={(e) => setTypeDoc(e.target.value)}
                                            disabled={isEdit}
                                        >
                                            <option value="">{t('common.select') || 'Select'}</option>
                                            {TYPE_DOC_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">{t('nav.administrator.templateDocuments.name') || 'Name'}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            maxLength={200}
                                            disabled={isEdit}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">{t('nav.administrator.templateDocuments.description') || 'Description'}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            maxLength={200}
                                            disabled={isEdit}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">{t('nav.administrator.templateDocuments.chooseFile') || 'Choose file'}</span>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            className="form-control"
                                            style={{ display: 'none' }}
                                            onChange={handleFileChange}
                                        />
                                        <button type="button" className="form-btn btn-green" onClick={handleChooseFile}>
                                            {t('nav.administrator.templateDocuments.chooseFile') || 'Choose file'}
                                        </button>
                                        {selectedFileName && (
                                            <div className="selected-file">
                                                <span className="file-name">{selectedFileName}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="modal-footer">
                        <button type="submit" className="btn btn-primary" form="templateDocumentsForm" disabled={loading}>
                            {t('common.save') || 'Save'}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.cancel') || 'Cancel'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}